import { useState } from "react";
import { Card, CardHead, Empty, Segmented, Skeleton, rupees } from "./parts";
import { avatarAt } from "./avatars";

// Medal tints for ranks 1-3, as in the design.
const MEDALS = {
  1: { bg: "oklch(0.92 0.08 85)", fg: "oklch(0.45 0.1 75)" },
  2: { bg: "oklch(0.93 0.01 275)", fg: "oklch(0.42 0.016 275)" },
  3: { bg: "oklch(0.92 0.05 55)", fg: "oklch(0.48 0.1 50)" },
};

// Rows from /leaderboardapi. "All time" rows carry id/userRank; "This month"
// rows carry <platform>UserId/totalAmountRank.
const normalize = (rows = [], monthly) =>
  rows.slice(0, 5).map((row) => ({
    id: monthly ? row.amazonUserId ?? row.flipkartUserId : row.id,
    rank: Number(monthly ? row.totalAmountRank : row.userRank),
    name: row.userName || "Anonymous",
    gender: row.gender,
    amount: Number(row.totalAmountSpent) || 0,
  }));

function Leaderboard({ loading, leaders, leaderId, userName, avatar, platformName }) {
  const [range, setRange] = useState("month");
  const rows = normalize(
    range === "month" ? leaders?.monthLeaderboardData : leaders?.allLeaderboardData,
    range === "month"
  );
  const top = Math.max(1, ...rows.map((row) => row.amount));

  return (
    <Card $pad="16px 18px 8px" id="leaderboard">
      <CardHead>
        <span>Leaderboard</span>
        <Segmented
          value={range}
          onChange={setRange}
          options={[
            { value: "month", label: "This month", icon: "calendar_month" },
            { value: "all", label: "All time", icon: "trophy" },
          ]}
        />
      </CardHead>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 12 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} $h={30} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Empty icon="leaderboard" title="No one here yet">
          {range === "month"
            ? `Nobody has ${platformName} orders this month yet.`
            : `Calculate your ${platformName} spend to join the board.`}
        </Empty>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rows.map((row, i) => {
            const you = row.id != null && String(row.id) === String(leaderId);
            const medal = MEDALS[row.rank];
            return (
              <div
                key={`${row.id}-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "30px 30px minmax(0,1fr) minmax(70px,26%) 90px",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 8px",
                  margin: "0 -8px",
                  borderTop: i ? "1px solid var(--line)" : 0,
                  borderRadius: you ? 10 : 0,
                  background: you ? "var(--acc-soft)" : "transparent",
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 12,
                    fontWeight: 600,
                    background: medal?.bg ?? "var(--surface2)",
                    color: medal?.fg ?? "var(--ink2)",
                  }}
                >
                  {row.rank}
                </span>
                <img
                  src={you && avatar ? avatar : avatarAt(row.gender, i)}
                  alt=""
                  style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--surface2)" }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {you ? userName || row.name : row.name}
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--ink3)" }}>{you ? "You" : `#${row.rank} spender`}</div>
                </div>
                <div style={{ height: 6, borderRadius: 3, background: "var(--surface2)", overflow: "hidden" }}>
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.max(4, (row.amount / top) * 100)}%`,
                      borderRadius: 3,
                      background: you ? "var(--acc)" : "var(--acc-mid)",
                    }}
                  />
                </div>
                <span
                  style={{
                    textAlign: "right",
                    fontSize: 13,
                    fontWeight: 600,
                    color: you ? "var(--acc)" : "var(--ink)",
                  }}
                >
                  {rupees(row.amount)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default Leaderboard;
